import { useRef } from "react";
import { Button, Modal } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { useReactToPrint } from "react-to-print";
import {
  convertToArabicDate,
  getMonthYearInArabic,
  getMonthYearInFrench,
} from "../utils/date";
import {
  getPaymentMethodInArabic,
  getPaymentMethodInFrench,
} from "../utils/payment";
import { getClientDisplayName } from "../utils/constants";
import LogoStationnement from "../assets/images/logo-stationnement.svg";
import PermissionRender from "./PermissionRender";

const SubscriptionReceiptPdf = ({
  subscriptionData,
  visible,
  onClose,
  paymentId,
}) => {
  const pdfRef = useRef();

  const pageStyle = `
    @page {
      size: A4;
      margin: 10mm;
    }
    @media print {
      html, body {
        margin: 0 !important;
        padding: 0 !important;
        width: 210mm;
        height: 297mm;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        overflow: hidden !important;
      }
      .printable-area {
        width: 100% !important;
        height: auto !important;
        max-height: 277mm !important;
        margin: 0 !important;
        padding: 10mm !important;
        border: none !important;
        box-shadow: none !important;
        overflow: hidden !important;
        font-size: 9pt !important;
        line-height: 1.3 !important;
        font-family: Arial, sans-serif !important;
        page-break-after: avoid !important;
        page-break-inside: avoid !important;
      }
      .printable-area * {
        color: #000 !important;
        page-break-inside: avoid !important;
      }
      .no-print {
        display: none !important;
      }
      .modal-content {
        display: none !important;
      }
    }
  `;

  const printNow = useReactToPrint({
    contentRef: pdfRef,
    documentTitle: "Recu_Paiement_Abonnement",
    pageStyle: pageStyle,
  });

  const handlePrint = () => {
    setTimeout(() => {
      if (pdfRef.current) {
        printNow();
      } else {
        console.error("PDF reference is not available.");
      }
    }, 200);
  };

  // Helper function to get client display name

  const payment =
    subscriptionData?.payments?.find((p) => p.id === paymentId) || null;
  const echeancePayment =
    payment?.details?.find((it) => it.type === "echeance") || null;
  const registration_fee =
    payment?.details?.find((it) => it.type === "registration_fee") || null;
  const reactivation_fee =
    payment?.details?.find((it) => it.type === "reactivation_fee") || null;
  const macaron_fee =
    payment?.details?.find((it) => it.type === "macaron_fee") || null;

  const echeanceDetail =
    subscriptionData?.echeances?.find(
      (it) => it.id === echeancePayment?.echeance_id
    ) || null;

  // Helper function to get agent name based on payment source
  const getAgentName = (payment) => {
    const source = payment?.source;
    
    if (source === "citizen_mobile") return "Mobile citoyen";
    if (source === "citizen_web") return "Web citoyen";
    
    // For agent or any other source, try to get the user's full_name
    if (payment?.user?.full_name) return payment.user.full_name;
    
    return "N/A";
  };

  // Determine if this is an agent payment (show "Nom Agent :" prefix)
  const isAgentPayment = payment?.source === "agent" || payment?.user?.full_name;

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width="700px"
      centered
    >
      <div className="mb-4 text-left no-print">
        <PermissionRender requiredPermission={["subscriptions", "print_receipt"]}>
        <Button
          onClick={handlePrint}
          type="primary"
          icon={<DownloadOutlined />}
        >
          Imprimer le reçu / طباعة الوصل
        </Button>
        </PermissionRender>
      </div>

      <div
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          overflow: "hidden",
          paddingBottom: "20px",
        }}
      >
        <div
          style={{
            transform: "scale(0.75)",
            transformOrigin: "top center",
          }}
        >
          <div
            ref={pdfRef}
            className="printable-area bg-white text-black"
            style={{
              fontFamily: "Arial, sans-serif",
              fontSize: "11px",
              margin: "0",
              padding: "20px",
              border: "1px solid #000",
              backgroundColor: "white",
              boxSizing: "border-box",
              width: "700px",
              minHeight: "800px",
            }}
          >
            {/* Header */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: "14px",
                borderBottom: "2px solid #000",
                paddingBottom: "14px",
              }}
            >
              <div
                style={{
                  textAlign: "left",
                  fontSize: "18px",
                  fontWeight: "bold",
                }}
              >
                <div>VILLE DE TANGER</div>
                <div>SERVICE DE STATIONNEMENT</div>
              </div>
              <div
                style={{
                  textAlign: "right",
                  fontSize: "18px",
                  fontWeight: "bold",
                }}
              >
                <div>مدينة طنجة</div>
                <div>خدمة الركن</div>
              </div>
            </div>

            {/* Title */}
            <div
              style={{
                textAlign: "center",
                marginBottom: "15px",
              }}
            >
              <div
                style={{
                  fontSize: "18px",
                  fontWeight: "bold",
                  textDecoration: "underline",
                  marginBottom: "5px",
                }}
              >
                {macaron_fee &&
                !echeancePayment &&
                !registration_fee &&
                !reactivation_fee
                  ? "REÇU DE PAIEMENT - FRAIS DE MACARON"
                  : "REÇU DE PAIEMENT D'ABONNEMENT"}
              </div>
              <div
                style={{
                  fontSize: "22px",
                  fontWeight: "bold",
                }}
              >
                {macaron_fee &&
                !echeancePayment &&
                !registration_fee &&
                !reactivation_fee
                  ? "وصل أداء - رسوم الماكرون"
                  : "وصل أداء الاشتراك"}
              </div>
            </div>

            {/* Divider */}
            <div
              style={{ borderBottom: "1px solid #000", marginBottom: "15px" }}
            ></div>

            {/* Receipt Number */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "15px",
                fontSize: "14px",
              }}
            >
              <div>
                <strong>REÇU N° : {payment?.reference || "N/A"}</strong>
              </div>
              <div style={{ textAlign: "right" }}>
                <strong style={{ direction: "rtl", display: "inline-block" }}>
                  رقم الوصل : {payment?.reference || "N/A"}
                </strong>
              </div>
            </div>

            {/* Dotted Line */}
            <div
              style={{ borderBottom: "1px dotted #000", marginBottom: "15px" }}
            ></div>

            {/* Client Information Section */}
            <div style={{ marginBottom: "15px", fontSize: "14px" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "8px",
                  fontWeight: "bold",
                  fontSize: "16px",
                }}
              >
                <div> Informations de l'abonné(e) : </div>
                <div style={{ textAlign: "right", direction: "rtl" }}>
                  معلومات المشترك(ة) :
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "5px",
                }}
              >
                <div>
                  Nom de l'abonné(e) :{" "}
                  <strong>
                    {getClientDisplayName(subscriptionData?.client)}
                  </strong>
                </div>
                <div style={{ textAlign: "right", direction: "rtl" }}>
                  اسم المشترك(ة) {" : "}
                  <strong>
                    {getClientDisplayName(subscriptionData?.client)}
                  </strong>{" "}
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "5px",
                }}
              >
                <div>
                  Numéro d'immatriculation :{" "}
                  <strong>
                    {subscriptionData?.vehicle?.matricule || "N/A"}
                  </strong>
                </div>
                <div style={{ textAlign: "right", direction: "rtl" }}>
                  رقم التسجيل{" : "}
                  <strong style={{ direction: "ltr", unicodeBidi: "embed" }}>
                    {subscriptionData?.vehicle?.matricule || "N/A"}
                  </strong>
                </div>
              </div>
            </div>

            {/* Dotted line separator */}
            <div
              style={{ borderBottom: "1px dotted #000", marginBottom: "15px" }}
            ></div>

            {/* Subscription Details Section */}
            <div style={{ marginBottom: "15px", fontSize: "14px" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "8px",
                  fontWeight: "bold",
                  fontSize: "16px",
                }}
              >
                <div> Détails d'abonnement :</div>
                <div style={{ textAlign: "right", direction: "rtl" }}>
                  تفاصيل الاشتراك :
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "5px",
                }}
              >
                <div>
                  Numéro du contrat :{" "}
                  <strong>{subscriptionData?.contract_number || "N/A"}</strong>
                </div>
                <div style={{ textAlign: "right", direction: "rtl" }}>
                  رقم العقد {" : "}
                  <strong>
                    {subscriptionData?.contract_number || "N/A"}
                  </strong>{" "}
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "5px",
                }}
              >
                <div>
                  Formule d'abonnement :
                  <strong>
                    {subscriptionData?.subscription_type?.name || "N/A"}
                  </strong>
                </div>
                <div style={{ textAlign: "right", direction: "rtl" }}>
                  صيغة الاشتراك {" : "}
                  <strong>
                    {subscriptionData?.subscription_type?.name || "N/A"}
                  </strong>
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "5px",
                }}
              >
                <div>
                  Période de validité :{" "}
                  <strong>
                    [
                    {subscriptionData?.start_date
                      ? subscriptionData.start_date
                          .split("-")
                          .reverse()
                          .join("/")
                      : "N/A"}{" "}
                    à{" "}
                    {subscriptionData?.end_date
                      ? subscriptionData?.end_date
                          .split("-")
                          .reverse()
                          .join("/")
                      : "N/A"}
                    ]
                  </strong>
                </div>
                <div style={{ textAlign: "right", direction: "rtl" }}>
                  مدة الاشتراك {" : "}
                  <strong>
                    [
                    {subscriptionData?.start_date
                      ? convertToArabicDate(subscriptionData.start_date)
                      : "N/A"}{" "}
                    إلى{" "}
                    {subscriptionData?.end_date
                      ? convertToArabicDate(subscriptionData?.end_date)
                      : "N/A"}
                    ]
                  </strong>{" "}
                </div>
              </div>
            </div>

            {/* Dotted line separator */}
            <div
              style={{ borderBottom: "1px dotted #000", marginBottom: "15px" }}
            ></div>

            {/* Payment Details Section */}
            <div style={{ marginBottom: "15px", fontSize: "14px" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "8px",
                  fontWeight: "bold",
                  fontSize: "16px",
                }}
              >
                <div> Détails de paiement : </div>
                <div style={{ textAlign: "right", direction: "rtl" }}>
                  تفاصيل الدفع {" : "}
                </div>
              </div>
              {/* Tarif appliqué - only show if there's an echeance payment */}
              {echeancePayment && (
                <>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: "5px",
                    }}
                  >
                    <div>
                      Tarif appliqué :{" "}
                      <strong>[{echeancePayment?.amount} DHS]</strong>
                    </div>
                    <div style={{ textAlign: "right", direction: "rtl" }}>
                      التسعيرة المطبقة {" : "}
                      <strong>[{echeancePayment?.amount} درهم]</strong>
                    </div>
                  </div>
                </>
              )}

              {registration_fee && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "5px",
                  }}
                >
                  <div>
                    Frais d'inscription :{" "}
                    <strong>[{registration_fee?.amount} DHS]</strong>
                  </div>
                  <div style={{ textAlign: "right", direction: "rtl" }}>
                    رسوم التسجيل {" : "}
                    <strong>[{registration_fee?.amount} درهم]</strong>
                  </div>
                </div>
              )}
              {reactivation_fee && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "5px",
                  }}
                >
                  <div>
                    Frais de réactivation :{" "}
                    <strong>[{reactivation_fee?.amount} DHS]</strong>
                  </div>
                  <div style={{ textAlign: "right", direction: "rtl" }}>
                    رسوم إعادة التفعيل {" : "}
                    <strong>[{reactivation_fee?.amount} درهم]</strong>
                  </div>
                </div>
              )}
              {macaron_fee && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "5px",
                  }}
                >
                  <div>
                    Frais de changement de macaron :{" "}
                    <strong>[{macaron_fee?.amount} DHS]</strong>
                  </div>
                  <div style={{ textAlign: "right", direction: "rtl" }}>
                    رسوم تغيير الماكرون {" : "}
                    <strong>[{macaron_fee?.amount} درهم]</strong>
                  </div>
                </div>
              )}

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "5px",
                }}
              >
                <div>
                  Montant payé : <strong>{payment.amount} DHS</strong>
                </div>
                <div style={{ textAlign: "right", direction: "rtl" }}>
                  المبلغ المدفوع {" : "}
                  <strong>{payment.amount} درهم</strong>
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "5px",
                }}
              >
                <div>
                  Dont TVA (20 %) :{" "}
                  <strong>
                    {Math.round(((payment.amount * 20) / 120) * 100) / 100} DHS
                  </strong>
                </div>
                <div style={{ textAlign: "right", direction: "rtl" }}>
                  منها ضريبة على القيمة المضافة {" (%20 ) : "}
                  <strong>
                    {Math.round(((payment.amount * 20) / 120) * 100) / 100} درهم
                  </strong>
                </div>
              </div>
              {/* Mensualité - only show if there's an echeance payment */}
              {echeancePayment && echeanceDetail && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "5px",
                  }}
                >
                  <div>
                    Mensualité :{" "}
                    <strong>
                      {getMonthYearInFrench(echeanceDetail?.start_date)}
                    </strong>
                  </div>
                  <div style={{ textAlign: "right", direction: "rtl" }}>
                    الدفعة الشهرية {" : "}
                    <strong>
                      {getMonthYearInArabic(echeanceDetail?.start_date)}
                    </strong>
                  </div>
                </div>
              )}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "5px",
                }}
              >
                <div>
                  Mode de paiement :{" "}
                  <strong>
                    {getPaymentMethodInFrench(payment?.payment_method) || "N/A"}
                  </strong>
                </div>
                <div style={{ textAlign: "right", direction: "rtl" }}>
                  طريقة الدفع {" : "}
                  <strong>
                    {getPaymentMethodInArabic(payment?.payment_method) || "N/A"}
                  </strong>
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "8px",
                }}
              >
                <div>
                  Date de paiement : <strong>{payment.payment_date}</strong>
                </div>
                <div style={{ textAlign: "right", direction: "rtl" }}>
                  تاريخ الدفع {" : "}
                  <strong>
                    {console.log(
                      payment.payment_date,
                      convertToArabicDate(payment.payment_date.split(" ")[0])
                    )}
                    {convertToArabicDate(payment.payment_date.split(" ")[0])}
                  </strong>
                </div>
              </div>
            </div>

            {/* Validity section - only show for echeance payments */}
            {echeancePayment && echeanceDetail && (
              <>
                {" "}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "15px",
                    fontSize: "14px",
                  }}
                >
                  <div>
                    <strong>
                      Valable jusqu'au {echeanceDetail?.end_date} à 23:59
                    </strong>
                  </div>
                  <div style={{ textAlign: "right", direction: "rtl" }}>
                    <strong>
                      صالح حتى {convertToArabicDate(echeanceDetail?.end_date)}{" "}
                      على الساعة 23:59
                    </strong>
                  </div>
                </div>
              </>
            )}

            {/* Dotted line separator */}
            <div
              style={{ borderBottom: "1px dotted #000", marginBottom: "15px" }}
            ></div>

            {/* Bottom Section */}
            <div
              style={{
                fontSize: "12px",
                marginBottom: "15px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "5px",
                }}
              >
                <div>
                  <strong>Patente : 33001036</strong>
                </div>
                <div style={{ textAlign: "right" }}>
                  <strong>ICE : 000831140000073 </strong>
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "15px",
                }}
              >
                <div>
                  {isAgentPayment ? (
                    <strong>Nom Agent : {getAgentName(payment)}</strong>
                  ) : (
                    <strong>{getAgentName(payment)}</strong>
                  )}
                </div>
                <div></div>
              </div>
            </div>

            {/* Vehicle and QR Code Section */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "15px",
              }}
            >
              <div style={{ textAlign: "center", height: "180px" }}>
                {/* <div
                  style={{
                    width: "70px",
                    height: "70px",
                    margin: "0 auto",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "8px",
                    marginBottom: "5px",
                  }}
                > */}
                <img
                  src={LogoStationnement}
                  alt="Logo Stationnement"
                  style={{ width: "220px", height: "220px" }}
                />
                {/* </div> */}
              </div>
            </div>

            {/* Service Info */}
            <div
              style={{
                textAlign: "center",
                fontSize: "12px",
                lineHeight: "1.3",
                marginBottom: "15px",
              }}
            >
              <div style={{ marginBottom: "5px" }}>
                <strong>SERVICE CLIENT / خدمة الزبناء</strong>
              </div>
              <div style={{ marginBottom: "5px" }}>
                <strong>05 39 32 51 33</strong>
              </div>
              <div style={{ marginBottom: "5px" }}>
                <strong>MERCI POUR VOTRE FIDÉLITÉ.</strong>
              </div>
              <div>
                <strong>شكرا لوفائكم</strong>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default SubscriptionReceiptPdf;
